import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, AlertCircle, FileText, Wrench, Menu, X, LogOut, Bell, Phone
} from 'lucide-react';

const ServiceAdvisorLayout = ({ children, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const menuItems = [
    { icon: Home, label: 'Dashboard', path: '/service_advisor/dashboard' },
    { icon: AlertCircle, label: 'Complaints', path: '/service_advisor/complaints' },
    { icon: FileText, label: 'Registered', path: '/service_advisor/registered' },
    { icon: Wrench, label: 'Mechanics', path: '/service_advisor/mechanics' },
    { icon: Phone, label: 'Call Report', path: '/service_advisor/call-report' },
  ];

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Mobile Header */}
      <div className="lg:hidden bg-gradient-to-r from-teal-500 to-green-600 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-white/20"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <h1 className="ml-3 text-xl font-bold">RoadServe</h1>
        </div>
        <button
          onClick={onLogout}
          className="p-2 rounded-lg hover:bg-white/20"
        >
          <LogOut size={20} />
        </button>
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:block bg-gradient-to-r from-teal-500 to-green-600 text-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Wrench size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold">RoadServe Service Advisor</h1>
              <p className="text-sm text-white/80">Welcome, {user?.name || 'Service Advisor'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button className="p-2 rounded-lg hover:bg-white/20 relative">
              <Bell size={20} />
              <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <button
              onClick={onLogout}
              className="px-4 py-2 rounded-lg hover:bg-white/20 flex items-center space-x-2"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex w-full">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:static inset-y-0 left-0 z-50
            bg-white border-r border-gray-200
            transform transition-transform duration-300 ease-in-out
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:translate-x-0 lg:transform-none lg:transition-none
            w-64 flex-shrink-0 h-screen overflow-y-auto pt-16 lg:pt-0
          `}
        >
          <nav className="p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className={`
                    w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition
                    ${active
                      ? 'bg-teal-50 text-teal-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="p-4 lg:p-6 max-w-full overflow-x-hidden">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default ServiceAdvisorLayout;

